import React, { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import { ArrowUpRight, ArrowDownRight, Search } from "lucide-react";
import { api, formatPct } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import Sparkline from "@/components/Sparkline";

export default function Markets() {
  const { fiat } = useOutletContext();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [fxRates, setFxRates] = useState({ USD: 1, CAD: 0.73, EUR: 1.08 });
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    const [m, fx] = await Promise.all([api.get("/markets"), api.get("/fx-rates")]);
    setAssets(m.data.assets);
    setFxRates(fx.data.rates);
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 60000);
    return () => clearInterval(i);
  }, [load]);

  const fxRate = fxRates[fiat] || 1;
  const filtered = assets.filter(
    (a) =>
      a.symbol.toLowerCase().includes(query.toLowerCase()) ||
      a.name.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-[#6E7587]">{t("imarkets.eyebrow")}</div>
          <h1 className="font-display text-3xl font-bold mt-1">{t("imarkets.title")}</h1>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E7587]" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("imarkets.search")}
            className="bg-[#0B0D12] border border-[#2A2E38] rounded-sm pl-9 pr-3 py-2 text-sm w-56 focus:border-[#7C5CFF] focus:outline-none"
          />
        </div>
      </div>

      <div className="border border-[#2A2E38] bg-[#141720]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] tracking-[0.2em] uppercase text-[#6E7587]">
              <tr className="border-b border-[#2A2E38]">
                <th className="text-left px-6 py-3 font-normal">{t("imarkets.col_asset")}</th>
                <th className="text-right px-6 py-3 font-normal">{t("imarkets.col_price")} ({fiat})</th>
                <th className="text-right px-6 py-3 font-normal">{t("imarkets.col_24h")}</th>
                <th className="px-6 py-3 hidden md:table-cell">{t("imarkets.col_7d")}</th>
                <th className="text-right px-6 py-3 font-normal hidden md:table-cell">{t("imarkets.col_mcap")}</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr
                  key={a.symbol}
                  data-testid={`market-row-${a.symbol}`}
                  onClick={() => navigate(`/app/markets/${a.symbol}`)}
                  className="border-b border-[#2A2E38] hover:bg-[#1C202B] transition-colors cursor-pointer"
                >
                  <td className="px-6 py-4 flex items-center gap-3">
                    {a.image && <img src={a.image} alt="" className="w-7 h-7" />}
                    <div>
                      <div className="font-medium">{a.name}</div>
                      <div className="text-xs text-[#6E7587] font-mono">{a.symbol}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right font-mono">
                    {(a.price_usd / fxRate).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </td>
                  <td className={`px-6 py-4 text-right font-mono ${a.change_24h >= 0 ? "text-[#7C9C69]" : "text-[#C75C5C]"}`}>
                    <span className="inline-flex items-center gap-1 justify-end">
                      {a.change_24h >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                      {formatPct(a.change_24h)}
                    </span>
                  </td>
                  <td className="px-6 py-3 hidden md:table-cell">
                    <Sparkline
                      data={a.sparkline}
                      width={120}
                      height={28}
                      color={a.change_24h >= 0 ? "#7C9C69" : "#C75C5C"}
                    />
                  </td>
                  <td className="px-6 py-4 text-right font-mono text-[#A3A9B8] hidden md:table-cell">
                    {(a.market_cap / fxRate).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <Link
                      to="/app/trade"
                      state={{ asset: a.symbol }}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs px-3 py-1.5 border border-[#2A2E38] rounded-sm hover:border-[#7C5CFF] hover:text-[#7C5CFF] transition-colors"
                    >
                      {t("imarkets.trade")}
                    </Link>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-[#6E7587]">No results</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
